import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { walkInService, ClientSearch } from '../../services/walkInService';
import BarberHeader from '../../components/barber/BarberHeader';

interface InitialState {
    startDate?: string;
    startTime?: string;
}

const WalkInSelectClientPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const initialState = (location.state as InitialState) || {};
    
    const [clientType, setClientType] = useState<'existing' | 'new' | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<ClientSearch[]>([]);
    const [searching, setSearching] = useState(false);
    const [clientName, setClientName] = useState('');

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchQuery.length >= 2) {
                searchClients();
            } else {
                setSearchResults([]);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const searchClients = async () => {
        try {
            setSearching(true);
            const results = await walkInService.searchClients(searchQuery);
            setSearchResults(results);
        } catch (error) {
            console.error('Error searching clients:', error);
        } finally {
            setSearching(false);
        }
    };

    const handleClientSelect = (client: ClientSearch) => {
        // Navigate to service selection with client data
        navigate('/barber/walk-in/service', {
            state: {
                ...initialState,
                clientId: client.id,
                clientName: `${client.firstName} ${client.lastName}`,
                isAnonymous: false
            }
        });
    };

    const handleNewClientContinue = () => {
        navigate('/barber/walk-in/service', {
            state: {
                ...initialState,
                clientId: null,
                clientName: clientName || null,
                isAnonymous: true
            }
        });
    };

    return (
        <>
            <BarberHeader
                title="🚶 Atender Ahora"
                subtitle="Paso 1: Seleccionar cliente"
                rightContent={
                    <button
                        className="btn btn-outline btn-sm"
                        onClick={() => navigate('/barber/dashboard')}
                    >
                        ✕ Cancelar
                    </button>
                }
            />
            <div className="container fade-in" style={{ maxWidth: '600px', margin: '0 auto', padding: '1rem' }}>

                {/* Step indicator */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    marginBottom: '2rem'
                }}>
                    <div style={{
                        width: '2rem', height: '2rem', borderRadius: '50%',
                        backgroundColor: 'var(--accent)', color: 'var(--text-on-accent)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: '700'
                    }}>1</div>
                    <div style={{ width: '3rem', height: '2px', backgroundColor: 'var(--secondary)', alignSelf: 'center' }} />
                    <div style={{
                        width: '2rem', height: '2rem', borderRadius: '50%',
                        backgroundColor: 'var(--secondary)', color: 'var(--text-secondary)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: '700'
                    }}>2</div>
                    <div style={{ width: '3rem', height: '2px', backgroundColor: 'var(--secondary)', alignSelf: 'center' }} />
                    <div style={{
                        width: '2rem', height: '2rem', borderRadius: '50%',
                        backgroundColor: 'var(--secondary)', color: 'var(--text-secondary)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: '700'
                    }}>3</div>
                </div>

                {/* Client type selection */}
                {clientType === null && (
                    <div>
                        <h2 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                            ¿El cliente ya está registrado?
                        </h2>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <button
                                onClick={() => setClientType('existing')}
                                style={{
                                    padding: '1.5rem',
                                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                    border: '2px solid var(--success)',
                                    borderRadius: '1rem',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '1rem',
                                    transition: 'all 0.2s',
                                    color: 'var(--text-primary)'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.1)';
                                }}
                            >
                                <span style={{ fontSize: '2rem' }}>✅</span>
                                <div style={{ textAlign: 'left' }}>
                                    <div style={{ fontWeight: '700', fontSize: '1.1rem' }}>Sí, buscar cliente</div>
                                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                        El cliente ya tiene cuenta en el sistema
                                    </div>
                                </div>
                            </button>
                            <button
                                onClick={() => setClientType('new')}
                                style={{
                                    padding: '1.5rem',
                                    backgroundColor: 'rgba(245, 158, 11, 0.1)',
                                    border: '2px solid var(--warning)',
                                    borderRadius: '1rem',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '1rem',
                                    transition: 'all 0.2s',
                                    color: 'var(--text-primary)'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(245, 158, 11, 0.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(245, 158, 11, 0.1)';
                                }}
                            >
                                <span style={{ fontSize: '2rem' }}>👤</span>
                                <div style={{ textAlign: 'left' }}>
                                    <div style={{ fontWeight: '700', fontSize: '1.1rem' }}>No, es cliente nuevo</div>
                                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                        Registrar como walk-in anónimo
                                    </div>
                                </div>
                            </button>
                        </div>
                    </div>
                )}

                {/* Search existing client */}
                {clientType === 'existing' && (
                    <div>
                        <button
                            onClick={() => setClientType(null)}
                            style={{
                                marginBottom: '1rem',
                                padding: '0.5rem 1rem',
                                backgroundColor: 'transparent',
                                border: 'none',
                                color: 'var(--text-secondary)',
                                cursor: 'pointer',
                                fontSize: '0.9rem'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-primary)'}
                            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
                        >
                            ← Volver
                        </button>
                        <h2 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>🔍 Buscar cliente</h2>
                        <input
                            type="text"
                            placeholder="Nombre, teléfono o email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '1rem',
                                backgroundColor: 'var(--surface)',
                                border: '2px solid var(--secondary)',
                                borderRadius: '0.75rem',
                                color: 'var(--text-primary)',
                                fontSize: '1rem',
                                marginBottom: '1rem',
                                outline: 'none'
                            }}
                            onFocus={(e) => e.target.style.borderColor = 'var(--accent)'}
                            onBlur={(e) => e.target.style.borderColor = 'var(--secondary)'}
                            autoFocus
                        />

                        {searching && (
                            <div style={{ textAlign: 'center', padding: '1rem', color: '#6b7280' }}>
                                Buscando...
                            </div>
                        )}

                        {!searching && searchResults.length === 0 && searchQuery.length >= 2 && (
                            <div style={{
                                textAlign: 'center',
                                padding: '2rem',
                                backgroundColor: 'var(--surface)',
                                borderRadius: '0.75rem',
                                border: '1px solid var(--secondary)',
                                color: 'var(--text-secondary)'
                            }}>
                                No se encontraron clientes
                            </div>
                        )}

                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {searchResults.map(client => (
                                <div
                                    key={client.id}
                                    onClick={() => handleClientSelect(client)}
                                    style={{
                                        padding: '1rem',
                                        backgroundColor: 'var(--surface)',
                                        borderRadius: '0.75rem',
                                        marginBottom: '0.75rem',
                                        cursor: 'pointer',
                                        border: '2px solid var(--secondary)',
                                        transition: 'all 0.2s',
                                        color: 'var(--text-primary)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(190, 242, 100, 0.1)';
                                        e.currentTarget.style.borderColor = 'var(--accent)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--surface)';
                                        e.currentTarget.style.borderColor = 'var(--secondary)';
                                    }}
                                >
                                    <div style={{ fontWeight: '700', fontSize: '1.1rem' }}>
                                        {client.firstName} {client.lastName}
                                    </div>
                                    <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                                        📞 {client.phoneNumber || 'Sin teléfono'} •
                                        📧 {client.email || 'Sin email'}
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '0.25rem' }}>
                                        {client.totalAppointments} visitas anteriores
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* New anonymous client */}
                {clientType === 'new' && (
                    <div>
                        <button
                            onClick={() => setClientType(null)}
                            style={{
                                marginBottom: '1rem',
                                padding: '0.5rem 1rem',
                                backgroundColor: 'transparent',
                                border: 'none',
                                color: 'var(--text-secondary)',
                                cursor: 'pointer',
                                fontSize: '0.9rem'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-primary)'}
                            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
                        >
                            ← Volver
                        </button>
                        <h2 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>👤 Cliente nuevo (Walk-in)</h2>

                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                                Nombre (opcional)
                            </label>
                            <input
                                type="text"
                                placeholder="Ej: Juan"
                                value={clientName}
                                onChange={(e) => setClientName(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '1rem',
                                    backgroundColor: 'var(--surface)',
                                    border: '2px solid var(--secondary)',
                                    borderRadius: '0.75rem',
                                    color: 'var(--text-primary)',
                                    fontSize: '1rem',
                                    outline: 'none'
                                }}
                                onFocus={(e) => e.target.style.borderColor = 'var(--accent)'}
                                onBlur={(e) => e.target.style.borderColor = 'var(--secondary)'}
                            />
                            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                                Si no ingresás nombre, se registrará como "Walk-in #ID"
                            </p>
                        </div>

                        <div style={{
                            padding: '1rem',
                            backgroundColor: 'rgba(245, 158, 11, 0.1)',
                            border: '1px solid rgba(245, 158, 11, 0.3)',
                            color: 'var(--warning)',
                            borderRadius: '0.75rem',
                            fontSize: '0.9rem',
                            marginBottom: '1.5rem'
                        }}>
                            ℹ️ Los datos completos se pueden vincular después cuando el cliente se registre en la app.
                        </div>

                        <button
                            onClick={handleNewClientContinue}
                            className="btn btn-primary"
                            style={{
                                width: '100%',
                                padding: '1rem',
                                fontSize: '1.1rem',
                                cursor: 'pointer'
                            }}
                        >
                            Continuar →
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

export default WalkInSelectClientPage;
